import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus, faBriefcase, faTrash, faPen, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { authFetch, formatDate } from '../utils/http';

interface Entry {
  id: string;
  learnershipId: string;
  learnershipTitle: string;
  entryDate: string;
  hoursWorked: number;
  description: string;
  mentorName: string | null;
  status: number; // 0 Submitted, 1 Approved, 2 Returned
  feedback: string | null;
}

interface CourseRef {
  learnershipId: string;
  title: string;
}

const STATUS_LABEL = ['Pending review', 'Approved', 'Returned for correction'];
const STATUS_BADGE = ['bg-amber-100 text-amber-700', 'bg-green-100 text-green-700', 'bg-red-100 text-red-700'];

const today = () => new Date().toISOString().slice(0, 10);

/** Learner Workplace Module logbook. */
export default function WorkplaceLogPage() {
  const { accessToken } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [courses, setCourses] = useState<CourseRef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [learnershipId, setLearnershipId] = useState('');
  const [entryDate, setEntryDate] = useState(today());
  const [hours, setHours] = useState('8');
  const [description, setDescription] = useState('');
  const [mentor, setMentor] = useState('');
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const [entriesRes, coursesRes] = await Promise.all([
      authFetch<Entry[]>(accessToken, '/api/workplace/mine'),
      authFetch<CourseRef[]>(accessToken, '/api/community/my-courses'),
    ]);
    if (entriesRes.ok && entriesRes.data) setEntries(entriesRes.data);
    else setError(entriesRes.message || 'Failed to load logbook.');
    if (coursesRes.ok && coursesRes.data) {
      setCourses(coursesRes.data);
      setLearnershipId((prev) => prev || coursesRes.data![0]?.learnershipId || '');
    }
    setIsLoading(false);
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setEntryDate(today());
    setHours('8');
    setDescription('');
    setMentor('');
    setFormError('');
  };

  const startEdit = (e: Entry) => {
    setEditingId(e.id);
    setLearnershipId(e.learnershipId);
    setEntryDate(e.entryDate.slice(0, 10));
    setHours(String(e.hoursWorked));
    setDescription(e.description);
    setMentor(e.mentorName ?? '');
    setShowForm(true);
  };

  const save = async () => {
    setFormError('');
    if (!learnershipId) {
      setFormError('Select a learnership.');
      return;
    }
    setIsSaving(true);
    const payload = {
      learnershipId,
      entryDate,
      hoursWorked: parseInt(hours, 10) || 0,
      description,
      mentorName: mentor || null,
    };
    const res = editingId
      ? await authFetch(accessToken, `/api/workplace/${editingId}`, { method: 'PUT', body: payload })
      : await authFetch(accessToken, '/api/workplace', { method: 'POST', body: payload });
    setIsSaving(false);
    if (!res.ok) {
      setFormError(res.message);
      return;
    }
    resetForm();
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this entry?')) return;
    const res = await authFetch(accessToken, `/api/workplace/${id}`, { method: 'DELETE' });
    if (!res.ok) setError(res.message);
    else load();
  };

  const approvedHours = entries.filter((e) => e.status === 1).reduce((sum, e) => sum + e.hoursWorked, 0);
  const totalHours = entries.reduce((sum, e) => sum + e.hoursWorked, 0);

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-950">Workplace Log</h1>
          <p className="text-sm text-brand-900/60 mt-1">Record your workplace activity for the Workplace Module</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
        >
          <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
          New entry
        </button>
      </div>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="card-surface p-4">
            <p className="text-xs text-brand-900/50">Total hours logged</p>
            <p className="text-2xl font-bold text-brand-950 mt-1">{totalHours}</p>
          </div>
          <div className="card-surface p-4">
            <p className="text-xs text-brand-900/50">Approved hours</p>
            <p className="text-2xl font-bold text-brand-950 mt-1">{approvedHours}</p>
          </div>
        </div>

        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faBriefcase} className="h-12 w-12 text-brand-300 mb-4" />
            <p className="text-brand-900/60">No entries yet. Add your first workplace entry.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((e) => (
              <div key={e.id} className="card-surface p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-brand-900/50">{e.learnershipTitle}</p>
                    <p className="text-sm font-semibold text-brand-950 mt-0.5">
                      {formatDate(e.entryDate)} · {e.hoursWorked} h
                    </p>
                    {e.mentorName && <p className="text-xs text-brand-900/50">Mentor: {e.mentorName}</p>}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${STATUS_BADGE[e.status]}`}>{STATUS_LABEL[e.status]}</span>
                </div>
                <p className="text-sm text-brand-900/80 mt-3 whitespace-pre-wrap">{e.description}</p>
                {e.feedback && (
                  <p className="text-xs text-brand-900/70 mt-3 bg-canvas-50 border border-canvas-200 rounded-lg p-3">
                    <span className="font-semibold">Trainer feedback:</span> {e.feedback}
                  </p>
                )}
                {e.status !== 1 && (
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => startEdit(e)}
                      className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5"
                    >
                      <FontAwesomeIcon icon={faPen} className="h-3 w-3" />
                      {e.status === 2 ? 'Correct & resubmit' : 'Edit'}
                    </button>
                    <button onClick={() => remove(e.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                      <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">{editingId ? 'Edit entry' : 'New workplace entry'}</h3>
              <button onClick={resetForm} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Learnership</label>
                <select
                  value={learnershipId}
                  onChange={(e) => setLearnershipId(e.target.value)}
                  disabled={!!editingId}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-canvas-100"
                >
                  {courses.map((c) => (
                    <option key={c.learnershipId} value={c.learnershipId}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Date</label>
                  <input
                    type="date"
                    value={entryDate}
                    max={today()}
                    onChange={(e) => setEntryDate(e.target.value)}
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Hours worked</label>
                  <input
                    type="number"
                    min={1}
                    max={24}
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">What did you do?</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Workplace mentor (optional)</label>
                <input
                  type="text"
                  value={mentor}
                  onChange={(e) => setMentor(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              {formError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{formError}</p>}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={resetForm} className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={isSaving}
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                {isSaving ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
